/**
 * <tf-sidebar> — Sidebar canonica condivisa tra 32 pagine app.
 *
 * Sostituisce gli `<aside id="ap-sidebar">` duplicati in 32 file
 * (4/5 hash MD5 identici → ~290 KB di duplicazione).
 *
 * Il rendering è skeleton-first: il componente emette un placeholder
 * con animate-pulse + brand logo. Il riempimento dinamico è delegato a
 * `app-sidebar.js` (legacy, già in produzione) tramite `applyMenu()`
 * che legge `tf-menu-config.js` per generare il menu reale.
 *
 * Migration path:
 *   - Sprint 3 (oggi): componente + skeleton 1 file invece di 32 inline
 *   - Sprint 5+: refactor `app-sidebar.js` → modulo TS che usa stesso pattern
 *
 * Uso:
 *   <tf-sidebar></tf-sidebar>
 *
 * (No attributes per ora — tutta la config è in tf-menu-config.js già)
 *
 * @since v9.16 (Sprint 3)
 */

const SIDEBAR_TEMPLATE = `
<aside id="ap-sidebar" class="fixed left-0 top-0 z-50 hidden w-[272px] flex-col bg-[#0f172a] transition-transform duration-300 lg:flex lg:translate-x-0" style="height:100dvh;height:100vh;-webkit-transform:translateX(-100%);transform:translateX(-100%)">

  <!-- Brand logo area -->
  <div class="flex h-[72px] items-center gap-3 px-6" id="sidebar-logo-area">
    <div id="sidebar-logo-default" class="tf-brand-logo">
      <svg class="tf-brand-logo-svg" viewBox="0 0 44 44" aria-label="TurboFattura">
        <defs>
          <linearGradient id="tf-bg-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fcd34d"/>
            <stop offset="50%" stop-color="#f59e0b"/>
            <stop offset="100%" stop-color="#b45309"/>
          </linearGradient>
          <radialGradient id="tf-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#fde68a" stop-opacity="0.7"/>
            <stop offset="60%" stop-color="#fbbf24" stop-opacity="0.25"/>
            <stop offset="100%" stop-color="#f59e0b" stop-opacity="0"/>
          </radialGradient>
          <linearGradient id="tf-bolt-fill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#fffbeb"/>
            <stop offset="100%" stop-color="#fde68a"/>
          </linearGradient>
        </defs>
        <rect width="44" height="44" rx="11" fill="url(#tf-bg-grad)"/>
        <circle class="tf-brand-halo" cx="22" cy="22" r="18" fill="url(#tf-halo)"/>
        <path class="tf-brand-bolt" d="M24.75 8.8L14.3 24.2h7.15l-2.2 11L29.7 19.8h-7.15l2.2-11z" fill="url(#tf-bolt-fill)" stroke="#ffffff" stroke-width="1.4" stroke-linejoin="round"/>
      </svg>
    </div>
    <span id="sidebar-brand-text" class="text-lg font-bold text-white">TurboFattura</span>
    <div id="sidebar-partner-brand" class="hidden flex-1 min-w-0">
      <div class="flex items-center gap-2.5">
        <img id="sidebar-partner-logo" class="hidden size-9 rounded-xl object-cover shadow-lg" alt="Studio logo" />
        <div class="min-w-0">
          <p id="sidebar-partner-nome" class="truncate text-sm font-bold text-white"></p>
          <p class="text-[9px] font-medium text-amber-400/70">powered by TurboFattura</p>
        </div>
      </div>
    </div>
    <button id="ap-sidebar-close" aria-label="Chiudi menu" class="ml-auto rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-white/10 hover:text-white lg:hidden">
      <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
    </button>
  </div>

  <!-- Nav placeholder skeleton — applyMenu() rebuilda da TF_MENU_CONFIG -->
  <nav class="flex-1 overflow-y-auto px-3 pt-6 pb-3">
    <div class="ap-nav-skeleton space-y-1 animate-pulse" data-tf-nav-skeleton>
      <div class="h-3 w-20 bg-white/[0.05] rounded mb-3 ml-3"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-3 w-20 bg-white/[0.05] rounded mt-7 mb-2 ml-3"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-3 w-20 bg-white/[0.05] rounded mt-7 mb-2 ml-3"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-3 w-20 bg-white/[0.05] rounded mt-7 mb-2 ml-3"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-3 w-20 bg-white/[0.05] rounded mt-7 mb-2 ml-3"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-9 bg-white/[0.04] rounded-xl"></div>
      <div class="h-3 w-20 bg-white/[0.05] rounded mt-7 mb-2 ml-3"></div>
    </div>
  </nav>

  <!-- User card placeholder (popolato da app-sidebar.js) -->
  <div id="ap-sidebar-userbox" class="border-t border-white/5 px-3 py-3">
    <div class="animate-pulse">
      <div class="flex items-center gap-3 px-3 py-2">
        <div class="h-9 w-9 rounded-full bg-white/[0.05]"></div>
        <div class="flex-1 space-y-1.5">
          <div class="h-3 w-3/4 bg-white/[0.05] rounded"></div>
          <div class="h-2 w-1/2 bg-white/[0.04] rounded"></div>
        </div>
      </div>
    </div>
  </div>
</aside>
`;

interface AppSidebarApi {
  apply?: () => void;
}

declare global {
  interface Window {
    applyMenu?: () => void;
    AppSidebar?: AppSidebarApi;
    /** Flag globale per evitare doppio apply */
    _tfMenuApplied?: boolean;
    /** Set quando il primo render reale è completato */
    _tfSidebarReady?: boolean;
  }
}

export class TfSidebarElement extends HTMLElement {
  connectedCallback(): void {
    // Render skeleton solo se non c'è già un #ap-sidebar (idempotenza)
    if (document.getElementById('ap-sidebar')) {
      this.style.display = 'none';
      return;
    }

    this.innerHTML = SIDEBAR_TEMPLATE;

    // ============================================================
    // ANTI-FLASH STRATEGY (Sprint 9.1)
    // ============================================================
    //
    // Problema legacy: skeleton visibile per ~50-200ms su DOMContentLoaded
    // prima che applyMenu() lo sostituisca. Causava "flash" del menu.
    //
    // Fix:
    // 1. Inject CSS che mantiene la nav stabile (no transition durante swap)
    // 2. Tenta applyMenu sincronamente IMMEDIATAMENTE (se app-sidebar.js
    //    è già caricato, bypassa lo skeleton del tutto)
    // 3. Polling rapido (10ms) per primi 500ms — appena applyMenu è
    //    disponibile, lo chiama (no aspetta DOMContentLoaded)
    // 4. Skeleton resta solo se network davvero lento (>500ms)
    // ============================================================

    this._injectAntiFlashStyles();
    this._tryApplyMenu();
  }

  private _injectAntiFlashStyles(): void {
    if (document.getElementById('tf-sidebar-anti-flash')) return;
    const style = document.createElement('style');
    style.id = 'tf-sidebar-anti-flash';
    style.textContent = `
      /* Anti-flash: nav durante rebuild non mostra transition */
      #ap-sidebar nav.tf-rebuilding > * {
        opacity: 0;
        transition: none !important;
      }
      #ap-sidebar nav:not(.tf-rebuilding) > * {
        opacity: 1;
        transition: opacity 120ms ease-out;
      }
      /* Nasconde lo skeleton non appena il menu reale è renderizzato
         (markato con data-tf-menu-real) — niente lampeggio */
      #ap-sidebar nav[data-tf-menu-real] .ap-nav-skeleton,
      #ap-sidebar nav[data-tf-menu-real] [data-tf-nav-skeleton] {
        display: none !important;
      }
      /* Reduced motion: niente fade */
      @media (prefers-reduced-motion: reduce) {
        #ap-sidebar nav > * { transition: none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Tenta di chiamare applyMenu() il prima possibile.
   *
   * Strategy:
   * 1. Sincronamente subito (cattura caso script già caricati)
   * 2. Polling 10ms per primi 500ms (script async/defer arrivano qui)
   * 3. Fallback DOMContentLoaded se ancora non disponibile
   */
  private _tryApplyMenu(): void {
    const w = window as Window;

    const tryNow = (): boolean => {
      if (w._tfSidebarReady) return true; // già fatto
      if (typeof w.applyMenu !== 'function') return false;

      try {
        w.applyMenu();
        // Marca nav come "menu reale" → CSS nasconde skeleton via attribute
        const nav = document.querySelector('#ap-sidebar nav');
        if (nav) nav.setAttribute('data-tf-menu-real', '1');
        w._tfSidebarReady = true;
        return true;
      } catch (e) {
        console.error('[tf-sidebar] applyMenu failed:', e);
        return false;
      }
    };

    // Attempt 1: sync
    if (tryNow()) return;

    // Attempt 2: polling rapido (10ms) per primi 500ms
    const startTime = Date.now();
    const pollInterval = setInterval(() => {
      if (tryNow() || Date.now() - startTime > 500) {
        clearInterval(pollInterval);
      }
    }, 10);

    // Attempt 3: fallback DOMContentLoaded (caso script lentissimi)
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          if (!w._tfSidebarReady) {
            clearInterval(pollInterval);
            tryNow();
          }
        },
        { once: true }
      );
    }

    // Attempt 4: ultimo fallback dopo window.load (script blocking)
    window.addEventListener(
      'load',
      () => {
        if (!w._tfSidebarReady) {
          clearInterval(pollInterval);
          tryNow();
        }
      },
      { once: true }
    );
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tf-sidebar')) {
  customElements.define('tf-sidebar', TfSidebarElement);
}
